/**
 * @file publicDeletion.ts
 * @brief "Delete your VERBLIZR account" without signing in (Google Play,
 *        BACKEND B7.3): the three calls of /api/public/account-deletion.
 *
 * @details
 * Plain fetch, not the app's API client: no token, no device id, no
 * custom headers, so the backend's public CORS answers a simple request.
 * The server's `message` is shown as is. POST / answers the same 200 whether
 * or not the account exists (no enumeration). The email never goes in a URL;
 * only the emailed one-time token does.
 * `fetchImpl` is injectable for node:test and the dev preview.
 * Pure (no app imports) so node:test can load it.
 *
 * @references
 * - BACKEND/src/features/auth/publicDeletion.routes.mjs
 * - scratch/claude-code-report.md "## B7" §3
 *
 * @author DES (prepared with Claude)
 * @date 22-10-2026
 */

import { FALLBACKS, configValue, publicConfigSnapshot } from "../../shared/publicConfig/publicConfig.ts";

export type PublicDeletionResult =
  | { ok: true; code: string; message: string; data: Record<string, unknown> }
  | { ok: false; status: number; code: string; message: string };

export type ConfirmInfo = {
  accountType: "personal" | "company";
  emailMasked: string;
  expiresAt: string | null;
  whatHappens: string;
};

type FetchLike = (url: string, init?: RequestInit) => Promise<Response>;

const FALLBACK: Record<string, string> = {
  network: "We couldn't reach VERBLIZR. Check your connection and try again.",
};

/** SUPPORT_EMAIL from GET /api/public/config (B23), else the address the page always gave. */
export function internalErrorMessage(): string {
  const email = configValue(publicConfigSnapshot(), "SUPPORT_EMAIL", FALLBACKS.SUPPORT_EMAIL);
  return `We couldn't finish this. Please email ${email} and we'll delete your account.`;
}

export function isLikelyEmail(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
}

export function createPublicDeletionApi(baseUrl: string, fetchImpl: FetchLike = (u, i) => fetch(u, i)) {
  const root = `${baseUrl.replace(/\/$/, "")}/api/public/account-deletion`;

  async function call(path: string, init?: RequestInit): Promise<PublicDeletionResult> {
    let res: Response;
    try {
      res = await fetchImpl(`${root}${path}`, init);
    } catch {
      return { ok: false, status: 0, code: "NETWORK", message: FALLBACK.network };
    }
    let body: Record<string, unknown> = {};
    try {
      body = (await res.json()) as Record<string, unknown>;
    } catch {
      // empty or not JSON
    }
    const code = typeof body.code === "string" ? body.code : res.ok ? "OK" : "INTERNAL_ERROR";
    const message =
      typeof body.message === "string" && body.message
        ? body.message
        : (FALLBACK[code] ?? internalErrorMessage());
    if (!res.ok || body.success === false) return { ok: false, status: res.status, code, message };
    return { ok: true, code, message, data: body };
  }

  const post = (path: string, body: unknown) =>
    call(path, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });

  return {
    /** Step 1: email me a confirm link. */
    request: (email: string) => post("/", { email: email.trim() }),
    /** Step 2: what confirming this link does. */
    lookup: (token: string) => call(`/confirm?token=${encodeURIComponent(token)}`),
    /** Step 3: confirm. */
    confirm: (token: string) => post("/confirm", { token }),
  };
}

export function parseConfirmInfo(data: Record<string, unknown>): ConfirmInfo | null {
  const whatHappens = typeof data.whatHappens === "string" ? data.whatHappens : null;
  if (!whatHappens) return null;
  return {
    accountType: data.accountType === "company" ? "company" : "personal",
    emailMasked: typeof data.emailMasked === "string" ? data.emailMasked : "",
    expiresAt: typeof data.expiresAt === "string" ? data.expiresAt : null,
    whatHappens,
  };
}
